import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import {
  EventSeverity,
  NotificationRuleCategory,
} from '@eppendorf/vnls-notifications-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getDeviceEventCategoryLabel } from '$shared/i18n/device-event-category-label';

import { useGetDeviceCategoriesList } from '$components/app-config/app-config.api';
import { InfoList } from '$features/devices/device-information/info-list';
import { getEventIconName } from '$features/monitoring/events/events.utils';
import {
  getCategoryIconName,
  getDeviceCategoriesQuery,
} from '$features/notifications/notification-category.utils';

import styles from './notification-detail-condition.module.scss';

type NotificationDetailConditionProps = {
  tags?: string[] | null;
  categories?: NotificationRuleCategory[];
  devices: DeviceDTO[];
  selectedDeviceType?: string;
};

function AdvancedNotificationDetailCondition({
  categories,
  devices,
  selectedDeviceType,
}: Required<Pick<NotificationDetailConditionProps, 'categories'>> &
  Pick<NotificationDetailConditionProps, 'devices' | 'selectedDeviceType'>) {
  const { t } = useTranslation();
  const { data } = useGetDeviceCategoriesList(
    getDeviceCategoriesQuery(devices, selectedDeviceType),
  );

  return (
    <InfoList>
      <InfoList.Title>{t('notificationDetail.condition')}</InfoList.Title>
      <div
        className="m-top-l flex flex__dir--column gap-xl"
        aria-label="notification-condition"
      >
        <p>{t('tagsCondition.advanced.introduction')}</p>
        {categories.map((condition) => {
          const category = data?.data.find((item) => item.key === condition.key);
          const subCategoryNames = condition.subCategories.map((key) =>
            getDeviceEventCategoryLabel(
              key,
              t,
              category?.subCategories.find((subCategory) => subCategory.key === key)
                ?.name,
            ),
          );

          return (
            <div key={condition.key} className={styles.conditionCard}>
              <Icon
                name={getCategoryIconName(condition.key)}
                size={IconSizeClasses.Small}
                className="bg-gray-900"
              />
              <span>
                <strong>
                  {getDeviceEventCategoryLabel(condition.key, t, category?.name)}
                </strong>
                {`: ${subCategoryNames.join(', ')}`}
              </span>
            </div>
          );
        })}
      </div>
    </InfoList>
  );
}

export function NotificationDetailCondition({
  tags,
  categories,
  devices,
  selectedDeviceType,
}: NotificationDetailConditionProps): ReactElement {
  const { t } = useTranslation();

  if (categories?.some((category) => category.subCategories.length > 0)) {
    return (
      <AdvancedNotificationDetailCondition
        categories={categories}
        devices={devices}
        selectedDeviceType={selectedDeviceType}
      />
    );
  }

  const showConditionMessage = (tag: string) => (
    <div key={`tag-${tag}`} className="flex flex__ac--flex-start">
      <Icon
        name={`event-${getEventIconName(tag)}`}
        size={IconSizeClasses.XSmall}
        key={`tag-${tag}`}
        className="m-right-m p-x-m"
      />
      <span className="p-top-xxs">
        <span className="font-weight-bold">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- we know it exists */}
          {t(`notificationTags.${tag}` as any)}
        </span>
        <span>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- we know it exists */}
          {t(`notificationTags.${tag}Message` as any)}
        </span>
      </span>
    </div>
  );

  return (
    <InfoList>
      <InfoList.Title>{t('notificationDetail.condition')}</InfoList.Title>
      <div
        className="m-top-l flex flex__dir--column gap-xl"
        aria-label="notification-condition"
      >
        <span>
          {t(`tagsCondition.notify`)}
          <span className="body-bold">{t(`tagsCondition.events`)} </span>
          {t(`tagsCondition.within`)}
          <span className="body-bold">{t(`tagsCondition.severity`)} </span>
        </span>
        {tags &&
          Object.values(EventSeverity)
            .filter((tag) => tags.includes(tag))
            .map((tag) => showConditionMessage(tag))}
      </div>
    </InfoList>
  );
}
